import React, { useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Spinner,
  Text,
  Image,
  useToken,
} from "@chakra-ui/react";
import { ProductService } from "./ProductService";
import Swal from "sweetalert2";
import hexToRgba from "hex-to-rgba";
import { Link } from "react-router-dom";
import { BsFunnel } from "react-icons/bs";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import FilterProductsModal from "./FilterProductsModal";
import { useQuery } from "react-query";
import Pagination from "../../components/Pagination"; // Import the generic Pagination component

const ProductsOverview = () => {
  const [productsList, setProductsList] = useState([]);
  const [segmentationJson, setSegmentationJson] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [mainColor] = useToken("colors", ["primary.main"]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const orgId = !!localStorage?.selectedOrg && JSON.parse(localStorage?.selectedOrg)?.token; 
  const pageSize = 50;
  const productsLimit = 10;

  const fetchProducts = useQuery(
    ["products", orgId, currentPage, segmentationJson],
    async () => {
      const data = ProductService.prepareDataJson(segmentationJson);
      const response = await ProductService.getTargetList(data, orgId, "product", currentPage, pageSize);
      setProductsList(response?.data ?? []);
      setTotalCount(response.data[0]?._total_count ?? 0);
      return response.data;
    },
    {
      staleTime: 0, // Ensure fresh data on page change
      retry: 1,
      enabled: !!orgId, // Only fetch if orgId exists
    }
  );

  // Handle CSV download
  const downloadCsv = async () => {
    if (fetchProducts.isFetching) return;

    try {
      const data = await ProductService.getChunkedTargetData(ProductService.targets.PRODUCTS, "segmentType", "segmentId");
      const csvBlob = new Blob([data], { type: "text/csv" });
      const downloadLink = document.createElement("a");
      downloadLink.download = `products_${Date.now()}.csv`;
      downloadLink.href = window.URL.createObjectURL(csvBlob);
      downloadLink.click();
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  // Create segment based on filtered products
  const createSegmentOnFilteredProducts = () => {
    if (totalCount > productsLimit) {
      Swal({
        title: "Are you sure?",
        text: `Max ${productsLimit} products can be selected, refine further or let us select top-selling products.`,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Create it anyway.",
      }).then((isConfirm) => {
        if (isConfirm) {
          createSegment();
        }
      });
    } else {
      createSegment();
    }
  };

  // Create a segment with selected product IDs
  const createSegment = () => {
    const productIds = productsList.slice(0, 100).map((product) => product.product_id);
    const criteria = {
      criteria_type: "query",
      data_type: "string",
      operator: "equals",
      operator_name: "Is",
      property: "product_id",
      property_name: "Product Id",
      values: productIds.map((productId) => ({ raw: productId })),
    };

    const segmentationJson = ProductService.getProductBuyerSegemntationJson();
    segmentationJson.product_criteria.criteria[0] = criteria;

    ProductService.openProductBuyerModal(segmentationJson);
  };

  return (
    <Box>
      <Text fontSize="2xl" mb={4}>Product Overview</Text>
      <PrimaryButton
        rightIcon={<BsFunnel />}
        title="Filter Products"
        onClick={() => setFilterModalOpen(true)}
        label="Filter Products"
      />
      <FilterProductsModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApplyFilters={(filters) => {
          setSegmentationJson(filters);
          setCurrentPage(1); // Reset to first page on filter change
          fetchProducts.refetch();
        }}
      />
      {fetchProducts.isLoading ? (
        <Spinner size="xl" />
      ) : fetchProducts.isError ? (
        <Text color="red.500">Error loading products. Please try again.</Text>
      ) : (
        <Box overflowY="auto" maxHeight="700px">
          <Table variant="simple">
            <Thead>
              <Tr>
                {ProductService?.getProductTableHeaders()?.map((header, index) => (
                  <Th key={index}>{header}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {productsList.length > 0 ? (
                productsList.map((product, index) => (
                  <Tr key={product.product_id || index}>
                    <Td w="100%" display="flex" gap="10px">
                      <Image h="50px" width="50px" src={product.image} alt={product.title} />
                      <Link to={`/audience/products/details/${product.product_id}`}>
                        <Text color="blue" textDecoration="underline" fontWeight="500">
                          {product.title}
                        </Text>
                      </Link>
                    </Td>
                    <Td>{product.product_id}</Td>
                    <Td>{product.product_type}</Td>
                    <Td>{product.published_at}</Td>
                    <Td>{product.updated_at}</Td>
                    <Td>{product.total_orders}</Td>
                    <Td>{product.gross_profit}</Td>
                    <Td>{product.handle}</Td>
                    <Td>{product.data_source}</Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={ProductService?.getProductTableHeaders()?.length || 1}>
                    <Text>No products found.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
      {!fetchProducts.isLoading && totalCount > 0 && (
        <Pagination
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          isDisabled={fetchProducts.isLoading || fetchProducts.isFetching}
        />
      )}
    </Box>
  );
};

export default ProductsOverview;